import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { VISION, MISSION, VALUES, WHY_US, ENTITIES, IMAGES } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        label="About Raritan Group"
        titleLines={["Consultancy", "meets", "execution"]}
        intro="A multidisciplinary organization uniting strategic planning, field implementation, and smart monitoring under one ecosystem to deliver measurable urban impact."
        testId="about-hero"
      />

      {/* Vision & Mission */}
      <section className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-px bg-raritan-border border border-raritan-border">
          <Reveal>
            <div className="bg-raritan-surface p-8 md:p-14 h-full">
              <SectionLabel className="mb-8">01 — Vision</SectionLabel>
              <p className="font-heading font-medium text-2xl md:text-3xl leading-tight tracking-tight text-raritan-ink">{VISION}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-raritan-green text-white p-8 md:p-14 h-full">
              <SectionLabel className="mb-8 text-white/60 [&>span]:bg-raritan-red">02 — Mission</SectionLabel>
              <p className="font-heading font-medium text-2xl md:text-3xl leading-tight tracking-tight">{MISSION}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
        <div className="max-w-[1400px] mx-auto">
          <SectionLabel className="mb-6">Core values</SectionLabel>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-raritan-ink mb-16">What we stand for</h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 border-t border-l border-raritan-border">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="p-7 border-r border-b border-raritan-border h-full hover:bg-raritan-green-soft transition-colors">
                  <span className="font-mono text-sm text-raritan-red">0{i + 1}</span>
                  <h3 className="mt-5 font-heading font-bold text-xl text-raritan-ink">{v.title}</h3>
                  <p className="mt-2 font-body text-sm text-raritan-muted leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Entities */}
      <section className="px-5 md:px-10 py-24 md:py-32 border-b border-raritan-border">
        <div className="max-w-[1400px] mx-auto">
          <SectionLabel className="mb-6">One ecosystem</SectionLabel>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-raritan-ink mb-16">Two entities, one mission</h2>
          <div className="grid md:grid-cols-2 gap-px bg-raritan-border border border-raritan-border">
            {ENTITIES.map((e, i) => (
              <Reveal key={e.id} delay={i * 0.1}>
                <div className="bg-raritan-surface p-8 md:p-12 h-full">
                  <div className="h-16 mb-6 flex items-center">
                    <img src={e.logo} alt={e.short} className="h-full w-auto max-w-[240px] object-contain" />
                  </div>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-raritan-red">{e.role}</span>
                  <h3 className="mt-5 font-heading font-bold text-2xl md:text-3xl tracking-tight text-raritan-ink">{e.short}</h3>
                  <p className="mt-2 font-mono text-xs text-raritan-muted">{e.name}</p>
                  <p className="mt-6 font-body text-raritan-muted leading-relaxed">{e.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why us with image */}
      <section className="px-5 md:px-10 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel className="mb-6">Why organizations trust us</SectionLabel>
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-5xl text-raritan-ink leading-[0.95] mb-8">Core strengths</h2>
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden border border-raritan-border">
                <img src={IMAGES.leadership} alt="Corporate meeting" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="border-t border-raritan-border">
              {WHY_US.map((w, i) => (
                <Reveal key={w.title} delay={i * 0.06}>
                  <div className="py-7 border-b border-raritan-border grid grid-cols-12 gap-4">
                    <span className="col-span-2 font-mono text-sm text-raritan-red">0{i + 1}</span>
                    <div className="col-span-10">
                      <h3 className="font-heading font-bold text-xl md:text-2xl text-raritan-ink">{w.title}</h3>
                      <p className="mt-2 font-body text-raritan-muted leading-relaxed">{w.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
